import { supabase } from './client';
import { LibraryItem, LibraryCollection } from '@/components/quotation/types';

export interface SupabaseLibraryItem {
  id: string;
  type: string;
  name: string;
  location?: string;
  data: any;
  created_at?: string;
  updated_at?: string;
}

export interface SupabaseLibraryCollection {
  id: string;
  name: string;
  description?: string;
  item_ids: string[];
  created_at?: string;
  updated_at?: string;
}

// Funções para conversão entre formatos
const toSupabaseItem = (item: LibraryItem): any => {
  // Map to the actual database schema
  return {
    id: item.id,
    type: item.type,
    name: item.title, // Map title to name
    location: item.location ? JSON.stringify(item.location) : null, // Convert location object to JSON string
    data: {
      ...item.data,
      description: item.description, // Store description in data
      tags: item.tags, // Store tags in data
      thumbnailUrl: item.thumbnailUrl, // Store thumbnailUrl in data
      fullLocation: item.location // Store full location object in data
    },
    updated_at: item.updatedAt ? new Date(item.updatedAt).toISOString() : undefined
  };
};

const fromSupabaseItem = (item: any): LibraryItem => {
  // Extract data from the database format to the app format
  const data = item.data || {};

  return {
    id: item.id,
    type: item.type as 'block' | 'restaurant' | 'accommodation' | 'tour' | 'transportation' | 'flight',
    title: item.name, // Map name to title
    description: data.description || '',
    data: data,
    tags: data.tags || [],
    thumbnailUrl: data.thumbnailUrl,
    location: data.fullLocation || tryParseLocation(item.location),
    createdAt: item.created_at ? new Date(item.created_at) : new Date(),
    updatedAt: item.updated_at ? new Date(item.updated_at) : new Date()
  };
};

// Helper function to try parsing location from string
const tryParseLocation = (locationStr: string | null) => {
  if (!locationStr) return {};
  try {
    return JSON.parse(locationStr);
  } catch (e) {
    return { formattedAddress: locationStr };
  }
};

const toSupabaseCollection = (collection: LibraryCollection): SupabaseLibraryCollection => {
  return {
    id: collection.id,
    name: collection.name,
    description: collection.description,
    item_ids: collection.items,
    updated_at: collection.updatedAt ? new Date(collection.updatedAt).toISOString() : undefined
  };
};

const fromSupabaseCollection = (collection: SupabaseLibraryCollection): LibraryCollection => {
  return {
    id: collection.id,
    name: collection.name,
    description: collection.description || '',
    items: collection.item_ids || [],
    createdAt: collection.created_at ? new Date(collection.created_at) : new Date(),
    updatedAt: collection.updated_at ? new Date(collection.updated_at) : new Date()
  };
};

export const libraryService = {
  // Métodos para gerenciar itens
  getItems: async (): Promise<LibraryItem[]> => {
    const { data, error } = await supabase
      .from('library_items')
      .select('*')
      .order('created_at', { ascending: false });
    
    if (error) {
      throw error;
    }
    
    return (data || []).map(fromSupabaseItem);
  },
  
  getItemById: async (id: string): Promise<LibraryItem | null> => {
    const { data, error } = await supabase
      .from('library_items')
      .select('*')
      .eq('id', id)
      .single();
    
    if (error) {
      return null;
    }
    
    return data ? fromSupabaseItem(data) : null;
  },
  
  searchItems: async (searchTerm: string): Promise<LibraryItem[]> => {
    const { data, error } = await supabase
      .from('library_items')
      .select('*')
      .or(`title.ilike.%${searchTerm}%,description.ilike.%${searchTerm}%`)
      .order('created_at', { ascending: false });
    
    if (error) {
      throw error;
    }
    
    return (data || []).map(fromSupabaseItem);
  },
  
  saveItem: async (item: Omit<LibraryItem, 'id' | 'createdAt' | 'updatedAt'>): Promise<LibraryItem> => {
    // Gerar um UUID para o item
    const id = crypto.randomUUID();
    
    const supabaseItem = toSupabaseItem({
      ...item,
      id,
      createdAt: new Date(),
      updatedAt: new Date()
    });
    
    try {
      const { data, error } = await supabase
        .from('library_items')
        .insert([supabaseItem])
        .select()
        .single();
      
      if (error) {
        throw error;
      }
      
      return fromSupabaseItem(data);
    } catch (error) {
      throw error;
    }
  },
  
  updateItem: async (id: string, updates: Partial<Omit<LibraryItem, 'id' | 'createdAt' | 'updatedAt'>>): Promise<LibraryItem> => {
    // Converter para formato do Supabase
    const supabaseUpdates: Partial<SupabaseLibraryItem> = {};
    
    // Handle direct field mappings
    if (updates.title !== undefined) supabaseUpdates.name = updates.title;
    
    // --- Simplified Data Handling ---
    // Start with the data provided in the updates, or an empty object
    const finalData = { ...(updates.data || {}) };

    // Explicitly overwrite specific fields within data from top-level updates
    if (updates.description !== undefined) finalData.description = updates.description;
    if (updates.tags !== undefined) finalData.tags = updates.tags;
    if (updates.thumbnailUrl !== undefined) finalData.thumbnailUrl = updates.thumbnailUrl;
    if (updates.location !== undefined) {
      // Ensure data.fullLocation gets the complete, correct location object
      finalData.fullLocation = updates.location;
      // Also update the separate location string field
      supabaseUpdates.location = JSON.stringify(updates.location);
    }

    // Assign the final constructed data object to the payload
    supabaseUpdates.data = finalData;
    // --- End Simplified Data Handling ---
    
    // Adicionar timestamp de atualização
    supabaseUpdates.updated_at = new Date().toISOString();
    
    const { data, error } = await supabase
      .from('library_items')
      .update(supabaseUpdates)
      .eq('id', id)
      .select()
      .single();
    
    if (error) {
      throw error;
    }
    
    return fromSupabaseItem(data);
  },
  
  deleteItem: async (id: string): Promise<boolean> => {
    const { error } = await supabase
      .from('library_items')
      .delete()
      .eq('id', id);
    
    if (error) {
      throw error;
    }
    
    return true;
  },
  
  // Métodos para gerenciar coleções
  getCollections: async (): Promise<LibraryCollection[]> => {
    const { data, error } = await supabase
      .from('library_collections')
      .select('*')
      .order('created_at', { ascending: false });
    
    if (error) {
      throw error;
    }
    
    return (data || []).map(fromSupabaseCollection);
  },
  
  getCollectionById: async (id: string): Promise<LibraryCollection | null> => {
    const { data, error } = await supabase
      .from('library_collections')
      .select('*')
      .eq('id', id)
      .single();
    
    if (error) {
      return null;
    }
    
    return data ? fromSupabaseCollection(data) : null;
  },
  
  createCollection: async (
    name: string, 
    description: string = '', 
    items: string[] = []
  ): Promise<LibraryCollection> => {
    // Gerar um UUID para a coleção
    const id = crypto.randomUUID();
    
    const collection: SupabaseLibraryCollection = {
      id,
      name,
      description,
      item_ids: items
    };
    
    const { data, error } = await supabase
      .from('library_collections')
      .insert([collection])
      .select()
      .single();
    
    if (error) {
      throw error;
    }
    
    return fromSupabaseCollection(data);
  },
  
  updateCollection: async (id: string, updates: Partial<Omit<LibraryCollection, 'id' | 'createdAt' | 'updatedAt'>>): Promise<LibraryCollection> => {
    // Converter para formato do Supabase
    const supabaseUpdates: Partial<SupabaseLibraryCollection> = {};
    
    if (updates.name !== undefined) supabaseUpdates.name = updates.name;
    if (updates.description !== undefined) supabaseUpdates.description = updates.description;
    if (updates.items !== undefined) supabaseUpdates.item_ids = updates.items;
    
    // Adicionar timestamp de atualização
    supabaseUpdates.updated_at = new Date().toISOString();
    
    const { data, error } = await supabase
      .from('library_collections')
      .update(supabaseUpdates)
      .eq('id', id)
      .select()
      .single();
    
    if (error) {
      throw error;
    }
    
    return fromSupabaseCollection(data);
  },
  
  deleteCollection: async (id: string): Promise<boolean> => {
    const { error } = await supabase
      .from('library_collections')
      .delete()
      .eq('id', id);
    
    if (error) {
      throw error;
    }
    
    return true;
  },
  
  // Métodos para gerenciar itens em coleções
  addItemToCollection: async (itemId: string, collectionId: string): Promise<boolean> => {
    // Buscar a coleção atual
    const collection = await libraryService.getCollectionById(collectionId);
    if (!collection) return false;
    
    // Verificar se o item já está na coleção
    if (collection.items.includes(itemId)) return true;
    
    // Adicionar o item à coleção
    const updatedItems = [...collection.items, itemId];
    await libraryService.updateCollection(collectionId, { items: updatedItems });
    
    return true;
  },
  
  removeItemFromCollection: async (itemId: string, collectionId: string): Promise<boolean> => {
    // Buscar a coleção atual
    const collection = await libraryService.getCollectionById(collectionId);
    if (!collection) return false;
    
    // Verificar se o item está na coleção
    if (!collection.items.includes(itemId)) return false;
    
    // Remover o item da coleção
    const updatedItems = collection.items.filter(id => id !== itemId);
    await libraryService.updateCollection(collectionId, { items: updatedItems });
    
    return true;
  },
  
  // Migrar itens e coleções do localStorage para o Supabase
  migrateLocalLibrary: async (): Promise<boolean> => {
    try {
      // Verificar se já realizamos a migração anteriormente
      const migrationCompleted = localStorage.getItem('voah_library_migration_completed');
      if (migrationCompleted === 'true') {
        return true;
      }

      // Migrar itens
      const localItems = localStorage.getItem('voah_library_items');
      if (localItems) {
        const items = JSON.parse(localItems);
        for (const item of items) {
          const supabaseItem = toSupabaseItem(item);
          await supabase
            .from('library_items')
            .upsert([supabaseItem], { 
              onConflict: 'id',
              ignoreDuplicates: false 
            });
        }
      }
      
      // Migrar coleções
      const localCollections = localStorage.getItem('voah_library_collections');
      if (localCollections) {
        const collections = JSON.parse(localCollections);
        for (const collection of collections) {
          const supabaseCollection = toSupabaseCollection(collection);
          await supabase
            .from('library_collections')
            .upsert([supabaseCollection], { 
              onConflict: 'id',
              ignoreDuplicates: false 
            });
        }
      }
      
      // Marcar que a migração foi concluída
      localStorage.setItem('voah_library_migration_completed', 'true');
      
      return true;
    } catch (error) {
      console.error('Erro durante migração da biblioteca:', error);
      return false;
    }
  },
  
  // Add this method to the libraryService object
  initializeLibraryTables: async (): Promise<boolean> => {
    try {
      // Em vez de verificar a estrutura detalhada, verificamos apenas se podemos acessar as tabelas
      const libraryItemsAccessible = await checkTableAccess('library_items');
      const collectionsAccessible = await checkTableAccess('library_collections');
      
      // Se ambas as tabelas estão acessíveis, retornamos sucesso
      // Não tentamos mais verificar e alterar a estrutura das tabelas
      if (libraryItemsAccessible && collectionsAccessible) {
        return true;
      }
      
      return false;
    } catch (error) {
      return false;
    }
  }
};

// Função auxiliar para verificar o acesso à tabela
async function checkTableAccess(tableName: string): Promise<boolean> {
  try {
    // Tentamos uma consulta simples para verificar se a tabela existe e está acessível
    const { data, error } = await supabase
      .from(tableName)
      .select('id')
      .limit(1);
      
    if (error) {
      return false;
    }
    
    // Se não houve erro, a tabela existe e está acessível
    return true;
  } catch (error) {
    return false;
  }
} 